import { Box, Theme, ThemeProvider, useMediaQuery, useTheme, Typography } from '@mui/material';
import { deepmerge } from '@mui/utils';

import {
	ContentfulBackground,
	ContentfulPageThemeOptions,
	HeadingTag,
	Maybe,
	TAGContentAlignment,
	TemplateWidth,
	DisableTemplatePaddingOn,
	TRoundedCornersSize,
} from '@/types';
import {
	ServiceLineHeroBodyCopy,
	ServiceLineHeroHeadline,
	ServiceLineHeroButtonCollection,
	Video,
	VideoAsset as TVideoAsset,
	ImageAsset as TImageAsset,
} from '@/types/generated';
import ServiceLineHeroContent from '@/snowflakes/teamtag/ServiceLineHero/ServiceLineHeroContent/ServiceLineHeroContent';
import ImageAsset from '@/components/ImageAsset';
import { IImage } from '@/components/ImageAsset/ImageAsset';
import { usePageThemeContext } from '@/context/PageThemeProvider.ctx';
import resolveContentfulHeadingTag from '@/utils/resolveContentfulHeadingTag';
import { resolvePageThemePalette } from '@/utils/resolvePageThemePalette';
import TAGSvgIcon from '@/components/TAGSvgIcon/TAGSvgIcon';
import { resolveContentfulBgColor, textToJumpLink } from '@/utils';
import VideoAsset from '@/components/VideoAsset';
import TemplateContainer from '@/components/TemplateContainer/TemplateContainer';

import {
	getContainerStyles,
	getMediaContainerStyles,
	mobileImageStyles,
	teamTagEyebrowOverrideStyles,
} from './ServiceLineHero.styles';

export interface IServiceLineHero {
	/**
	 * The headline for the template.
	 *
	 * @type {Maybe<ServiceLineHeroHeadline>}
	 * @memberof IServiceLineHero
	 */
	headline?: Maybe<ServiceLineHeroHeadline>;
	/**
	 * The eyebrow Heading Tag for the template.
	 *
	 * @type {Maybe<HeadingTag>}
	 * @memberof IServiceLineHero
	 */
	eyebrowHtag?: Maybe<HeadingTag>;
	/**
	 * The eyebrow for the template.
	 *
	 * @type {Maybe<string>}
	 * @memberof IServiceLineHero
	 */
	eyebrow?: Maybe<string>;

	/**
	 * The body copy for the template.
	 *
	 * @type {Maybe<ServiceLineHeroBodyCopy>}
	 * @memberof IServiceLineHero
	 */
	bodyCopy?: Maybe<ServiceLineHeroBodyCopy>;

	/**
	 * The collection of buttons to display. Maximum of 2 buttons.
	 *
	 * @type {Maybe<ServiceLineHeroButtonCollection>}
	 * @memberof IServiceLineHero
	 */
	buttonCollection?: Maybe<ServiceLineHeroButtonCollection>;

	/**
	 * Width of the ServiceLineHero template. Can be Full or Inset.
	 *
	 * @type {Maybe<TemplateWidth>}
	 * @memberof IServiceLineHero
	 * @default 'Full'
	 */
	templateWidth?: Maybe<TemplateWidth>;

	/**
	 * Video Asset with video for Desktop and Mobile
	 *
	 * @type {Maybe<VideoAsset>}
	 * @memberof IServiceLineHero
	 */
	videoAsset?: Maybe<TVideoAsset>;

	/**
	 * An option to hide the hero image on mobile.
	 *
	 * @type {Maybe<boolean>}
	 * @memberof IServiceLineHero
	 */
	hideHeroOnMobile?: Maybe<boolean>;
	/**
	 * An option to display the hero image first and the supporting text second, or vice versa (Mobile Only).
	 *
	 * @type {Maybe<boolean>}
	 * @memberof IServiceLineHero
	 */
	imgDisplaysFirstOnMobile?: Maybe<boolean>;
	/**
	 * An option to display the hero image first and the supporting text second, or vice versa (Desktop Only).
	 *
	 * @type {Maybe<boolean>}
	 * @memberof IServiceLineHero
	 */
	imgDisplaysFirstOnDesktop?: Maybe<boolean>;
	/**
	 * An option to display a border radius on the image.
	 *
	 * @type {Maybe<boolean> | null}
	 * @memberof IServiceLineHero
	 */
	imageWithBorderRadius?: Maybe<boolean> | null;
	/**
	 * An option to set the size of rounded corner on media. (only works when imageWithBorderRadius enabled)
	 *
	 * @type {Maybe<TRoundedCornersSize>}
	 * @memberof IServiceLineHero
	 * @default 'Large'
	 */
	mediaRoundedCornerSize?: Maybe<TRoundedCornersSize>;
	/**
	 * An option to display different background colors.
	 *
	 * @type {Maybe<string>}
	 * @memberof IServiceLineHero
	 */
	backgroundColor?: Maybe<string>;
	/**
	 * An option to display different background colors for Mobile.
	 *
	 * @type {Maybe<string>}
	 * @memberof IServiceLineHero
	 */
	backgroundColorMobile?: Maybe<string>;
	/**
	 * An option to either 'Center' or 'Left' align the content
	 * @type {Maybe<TAGContentAlignment>}
	 * @memberof IServiceLineHero
	 * @default 'Left'
	 */
	contentAlign?: Maybe<TAGContentAlignment> | null;
	/**
	 * An option to disable padding on top and/or the bottom of the template.
	 *
	 * @type {Array<'Top' | 'Bottom'> | undefined}
	 * @memberof IServiceLineHero
	 * @default ['Top', 'Bottom']
	 */
	disablePaddingOn?: DisableTemplatePaddingOn;
	/**
	 * Used to identify a component on a page for the purpose of jumping to it using the anchor link.
	 *
	 * @type {Maybe<string>}
	 * @memberof IServiceLineHero
	 */
	anchorId?: Maybe<string>;
	/**
	 * Brandfolder Image with focal point.
	 *
	 * @type {Maybe<ImageAsset>}
	 * @memberof IServiceLineHero
	 */
	imageAsset?: Maybe<TImageAsset>;
}
export default function ServiceLineHero({
	headline,
	eyebrowHtag = 'normal',
	eyebrow,
	bodyCopy,
	templateWidth = 'Full',
	buttonCollection,
	videoAsset,
	hideHeroOnMobile = false,
	imgDisplaysFirstOnMobile = false,
	imgDisplaysFirstOnDesktop = false,
	imageWithBorderRadius = true,
	mediaRoundedCornerSize = 'Large',
	backgroundColor = 'White',
	backgroundColorMobile = 'White',
	contentAlign = 'Left',
	disablePaddingOn = ['Top', 'Bottom'],
	anchorId,
	imageAsset,
}: Readonly<IServiceLineHero>) {
	const theme = useTheme();
	const { palette } = theme;

	const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
	const pageTheme = usePageThemeContext();
	// NOTE: Fixes issue where storybook was not respecting Page Theme flag
	const componentTheme = theme.tagFeatures?.PAGE_THEME_ENABLED
		? deepmerge(theme, {
				palette: resolvePageThemePalette(pageTheme as ContentfulPageThemeOptions, palette),
		  })
		: theme;
	const backgroundToUse = isSmallScreen ? backgroundColorMobile : backgroundColor;
	const contentfulBackgroundColor = resolveContentfulBgColor(
		backgroundToUse as ContentfulBackground,
		theme,
		componentTheme.palette.backgrounds?.default
	);

	const isDarkBackground = !!backgroundToUse?.includes('Dark');

	let bgColor;
	switch (anchorId) {
		case 'Aspen Dental':
			bgColor = 'linear-gradient(to bottom, #F7FFFF 75%, #FFF7F9 85%)';
			break;
		case 'Chapter':
			bgColor = 'linear-gradient(to bottom, #FFF7F9 75%, #EDF4FD 85%)';
			break;
		case 'ClearChoice':
			bgColor = 'linear-gradient(to bottom, #EDF4FD 75%, #FCEFD8 85%)';
			break;
		case 'Wellnow':
			bgColor = 'linear-gradient(to bottom, #FCEFD8 75%, #FFF6F5 85%)';
			break;
		case 'Lovet':
			bgColor = '#FFF6F5';
			break;
		default:
			bgColor = 'white';
			break;
	}

	let svgData;
	// Define the SVG data based on the anchorId
	switch (anchorId) {
		case 'Aspen Dental':
			svgData = (
				<TAGSvgIcon
					icon={'AspenDentalBrandLogo'} // Use the correct string identifier for the icon
					width={210} // Set the width of the icon
					height={38} // Set the height of the icon
					viewBox={'0 0 210 38'} // Ensure the viewBox is set correctly for the icon
					dataTestId={`brands-image`} // Unique test ID for each brand icon
				/>
			);
			break;
		case 'Chapter':
			svgData = (
				<TAGSvgIcon
					icon={'ChapterBrandLogo'} // Use the correct string identifier for the icon
					width={210} // Set the width of the icon
					height={59} // Set the height of the icon
					viewBox={'0 0 210 59'} // Ensure the viewBox is set correctly for the icon
					dataTestId={`brands-image`} // Unique test ID for each brand icon
				/>
			);
			break;
		case 'ClearChoice':
			svgData = (
				<TAGSvgIcon
					icon={'ClearChoiceBrandLogo'} // Use the correct string identifier for the icon
					width={258} // Set the width of the icon
					height={58} // Set the height of the icon
					viewBox={'0 0 258 58'} // Ensure the viewBox is set correctly for the icon
					dataTestId={`brands-image`} // Unique test ID for each brand icon
				/>
			);
			break;
		case 'Wellnow':
			svgData = (
				<TAGSvgIcon
					icon={'WellnowBrandLogo'} // Use the correct string identifier for the icon
					width={280} // Set the width of the icon
					height={68} // Set the height of the icon
					viewBox={'0 0 280 68'} // Ensure the viewBox is set correctly for the icon
					dataTestId={`brands-image`} // Unique test ID for each brand icon
				/>
			);
			break;
		case 'Lovet':
			svgData = (
				<TAGSvgIcon
					icon={'LovetBrandLogo'} // Use the correct string identifier for the icon
					width={210} // Set the width of the icon
					height={43} // Set the height of the icon
					viewBox={'0 0 210 43'} // Ensure the viewBox is set correctly for the icon
					dataTestId={`brands-image`} // Unique test ID for each brand icon
				/>
			);
			break;
		default:
			break;
	}

	return (
		<ThemeProvider theme={componentTheme}>
			<TemplateContainer
				dataTestId="section_service_line_hero"
				containerSx={getContainerStyles(
					componentTheme,
					disablePaddingOn,
					!!imgDisplaysFirstOnMobile,
					!!imgDisplaysFirstOnDesktop,
					bgColor
				)}
				contentfulBackgroundColor={contentfulBackgroundColor}
				templateWidth={(templateWidth as TemplateWidth) || 'Full'}
				{...(anchorId && { id: textToJumpLink(anchorId) })}
			>
				{isSmallScreen ? (
					<Box
						sx={{ marginX: '20px', marginBottom: '20px' }}
						data-testid={'service_line_hero_content_container'}
					>
						{eyebrow && (
							<Typography
								data-testid="service_line_hero_eyebrow_text"
								variant="overline"
								component={resolveContentfulHeadingTag(eyebrowHtag as HeadingTag)}
								color={isDarkBackground ? 'text.light' : 'text.secondary'}
								sx={teamTagEyebrowOverrideStyles}
							>
								{eyebrow}
							</Typography>
						)}
						<Box
							sx={{
								height: '2px',
								width: '100%',
								bgcolor: '#A7ADB0',
								marginBottom: '3.75rem',
								marginTop: '1.063rem',
							}}
						/>
						{svgData}
					</Box>
				) : null}
				<ServiceLineHeroMedia
					key={1}
					videoAsset={videoAsset}
					imageAsset={imageAsset}
					imageWithBorderRadius={imageWithBorderRadius}
					isSmallScreen={isSmallScreen}
					imgDisplaysFirstOnDesktop={imgDisplaysFirstOnDesktop}
					mediaRoundedCornerSize={mediaRoundedCornerSize}
					componentTheme={componentTheme}
					hideHeroOnMobile={!!hideHeroOnMobile}
				/>
				<ServiceLineHeroContent
					key={0}
					headline={headline}
					eyebrowHtag={eyebrowHtag}
					eyebrow={eyebrow}
					bodyCopy={bodyCopy}
					buttons={buttonCollection?.items}
					isDarkBackground={isDarkBackground}
					contentAlign={contentAlign}
					isSmallScreen={isSmallScreen}
					svgData={svgData}
				/>
			</TemplateContainer>
		</ThemeProvider>
	);
}

interface ServiceLineHeroMediaProps {
	videoAsset?: Maybe<TVideoAsset>;
	imageAsset?: Maybe<TImageAsset>;
	imageWithBorderRadius: Maybe<boolean>;
	isSmallScreen: boolean;
	imgDisplaysFirstOnDesktop?: Maybe<boolean>;
	mediaRoundedCornerSize?: Maybe<TRoundedCornersSize>;
	componentTheme: Theme;
	hideHeroOnMobile: boolean;
}

function ServiceLineHeroMedia({
	videoAsset,
	imageWithBorderRadius,
	isSmallScreen,
	imgDisplaysFirstOnDesktop,
	mediaRoundedCornerSize,
	componentTheme,
	hideHeroOnMobile,
	imageAsset,
}: Readonly<ServiceLineHeroMediaProps>) {
	if (hideHeroOnMobile && isSmallScreen) {
		return null;
	}
	if (videoAsset) {
		return (
			<Box
				sx={getMediaContainerStyles({
					theme: componentTheme,
					mediaWithBorderRadius: imageWithBorderRadius,
					isVideo: true,
					imgDisplaysFirstOnDesktop,
					mediaRoundedCornerSize,
				})}
				key={videoAsset.sys.id}
				data-testid="service_line_hero_video_asset"
			>
				<VideoAsset
					desktopVideo={videoAsset.desktopVideo as Video}
					mobileVideo={(videoAsset.mobileVideo as Video) || undefined}
				/>
			</Box>
		);
	}

	return (
		<Box
			sx={getMediaContainerStyles({
				theme: componentTheme,
				mediaWithBorderRadius: imageWithBorderRadius,
				isVideo: false,
				imgDisplaysFirstOnDesktop,
				mediaRoundedCornerSize,
			})}
			// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
			key={imageAsset?.desktopImage?.image?.id}
			data-testid={`service_line_hero_${imageAsset ? 'image_asset' : 'focal_point_image'}`}
		>
			{imageAsset ? (
				<ImageAsset
					desktopImage={imageAsset?.desktopImage as IImage}
					mobileImage={imageAsset?.mobileImage as IImage}
					dataTestId={'img_asset_service_line_hero'}
					loading="eager"
					{...(isSmallScreen && { imageSx: mobileImageStyles })}
					imageSx={{ height: 'auto', width: { md: 'auto', sm: '100%' }, minHeight: '0%', minWidth: '0%' }}
				/>
			) : null}
		</Box>
	);
}
